import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { RootState } from "../../../libraries/state_reducers/store";
import { useSelector } from "react-redux";
import {
  toInputDate,
  toSaveDate,
  toSaveTime,
} from "../../../service/date_service/date_service";
import UtilityHelper from "../../../service/utilities/UtilityHelper";
import { AlertMesage } from "../../../widget/common/ui/Alerts";
import GuestUserFormProvider from "./GuestUserFormProvider";
import StorageKeys from "../../../const/StorageKeys";
import CacheStorage from "../../../service/storage/CacheStorage";
import { appRoutes } from "../../../routes/routes";
import Loader from "../../../widget/common/ui/Loader";
import { useLocation } from "react-router-dom";
import AppTranslation from "../../../service/app_translation/AppTranslation";
import ShowToast from "../../../widget/common/ui/Toast";
import { phoneMobileDefault } from "../../../const/MobileConstant";
import { CountryCodeList } from "../../../model/country_code/CountryCodeModel";

type GuestUserFormContextType = {
  submitForm: boolean;
  fieldsValid: FormValidType;

  nameField: string;
  emailField: string;

  phoneField: string;
  phoneCodeField: string;
  phoneCodeIdField: string;

  dateField: string;
  timeField: string;

  subjectField: string;
  messageField: string;

  maxWords: number;

  handleOnChange: (
    name: string,
    value: string,
    isValid: boolean | undefined
  ) => void;

  handleSubmit: () => void;

  applyCountryCode: (countryCodeDetail: CountryCodeList) => void;
};

type FormValidType = {
  name: boolean;
  email: boolean;
  date: boolean;
  phone: boolean;
  time: boolean;
  subject: boolean;
  message: boolean;
};

const GuestUserFormContext = createContext<
  GuestUserFormContextType | undefined
>(undefined);

export const GuestUserFormContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const location = useLocation();

  const maxWords: number = 2000;

  const dateSelected: Date | null = useSelector(
    (state: RootState) => state.datePickerAlert.dateSelected
  );

  /* const countryCodeDetail = useSelector(
    (state: RootState) => state.countryCode.countryCodeDetail
  ); */

  const fieldsValidDefault: FormValidType = {
    name: false,
    email: false,
    date: false,
    phone: false,
    time: false,
    subject: false,
    message: false,
  };

  const branchId: string = CacheStorage.getValue(StorageKeys.branch_id);

  const fieldsValidRef = useRef<FormValidType>(fieldsValidDefault);

  const [fieldsValid, setFieldsValid] =
    useState<FormValidType>(fieldsValidDefault);

  const [nameField, setNameField] = useState<string>("");
  const [emailField, setEmailField] = useState<string>("");

  const [phoneField, setPhoneField] = useState<string>("");
  const [phoneCodeField, setPhoneCodeField] = useState<string>(
    phoneMobileDefault.phone_code
  );
  const [phoneCodeIdField, setPhoneCodeIdField] = useState<string>(
    phoneMobileDefault.phone_code_id
  );

  const [dateField, setDateField] = useState<string>("");
  const [timeField, setTimeField] = useState<string>("");

  const [subjectField, setSubjectField] = useState<string>("");
  const [messageField, setMessageField] = useState<string>("");

  const [submitForm, setSubmitForm] = useState<boolean>(false);

  const [formType, setFormType] = useState<"1" | "4">("1"); // 1-feedback, 4-general

  const setupFormType = () => {
    const pathArr = location.pathname.split("/");
    const pathName = pathArr[pathArr.length - 1];

    setFormType(pathName === "form-general" ? "4" : "1");
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === "name") {
      setNameField(fieldValue);
      fieldsValidRef.current.name = isValid ?? false;
    } else if (fieldName === "email") {
      setEmailField(fieldValue);
      fieldsValidRef.current.email = isValid ?? false;
    } else if (fieldName === "phone_number") {
      setPhoneField(fieldValue);
      fieldsValidRef.current.phone = isValid ?? false;
    } else if (fieldName === "date") {
      setDateField(fieldValue);
      fieldsValidRef.current.date = isValid ?? false;
    } else if (fieldName === "time") {
      setTimeField(fieldValue);
      fieldsValidRef.current.time = isValid ?? false;
    } else if (fieldName === "subject") {
      setSubjectField(fieldValue);
      fieldsValidRef.current.subject = isValid ?? false;
    } else if (fieldName === "message") {
      setMessageField(fieldValue);
      fieldsValidRef.current.message = isValid ?? false;
    }

    setFieldsValid(fieldsValidRef.current);
    setSubmitForm(false);
  };

  const applyCountryCode = (countryCodeDetail: CountryCodeList) => {
    if (countryCodeDetail != null) {
      setPhoneCodeField(countryCodeDetail?.country_phonecode!);
      setPhoneCodeIdField(countryCodeDetail?.country_id!);
    }
  };

  const handleSubmit = async () => {
    setSubmitForm(true);
    console.log(`setSubmitForm`);

    if (
      ![
        fieldsValidRef.current.name,
        //fieldsValidRef.current.email,
        //fieldsValidRef.current.phone,
        fieldsValidRef.current.date,
        fieldsValidRef.current.time,
        fieldsValidRef.current.subject,
        fieldsValidRef.current.message,
      ].includes(false)
    ) {
      if (
        (fieldsValidRef.current.email === true &&
          UtilityHelper.isEmail(emailField)) ||
        (fieldsValidRef.current.phone === true &&
          UtilityHelper.phoneNumberValid(phoneField) &&
          UtilityHelper.isNotEmpty(phoneCodeIdField))
      ) {
        if (UtilityHelper.countWords(messageField) > maxWords) {
          ShowToast.warning(AppTranslation.text("character_exceeded"));
        } else {
          submitFormAPI();
        }
      } else {
        AlertMesage({
          title: "guest_user_phone_email_missing_msg",
        });
      }
    }
  };

  const submitFormAPI = async () => {
    Loader.modal();
    const statusResult: boolean = await GuestUserFormProvider.setFormAPI(
      branchId,
      formType,
      {
        name: nameField,
        email: emailField,
        mobile_code_id: phoneCodeIdField,
        mobile_no: phoneField,
        date: toSaveDate(dateField),
        time: toSaveTime(timeField),
        subject: subjectField,
        description: messageField,
      }
    );
    Loader.modal(false);

    if (statusResult) {
      AlertMesage({
        title: "guest_user_enquiry_success_alert_msg",
        onClick() {
          GuestUserFormProvider.navigate(appRoutes.routeNameHomeGuest);
        },
      });
    }
  };

  useEffect(() => {
    if (dateSelected != null) {
      setDateField(toInputDate(dateSelected!));
      fieldsValidRef.current.date = true;
      setSubmitForm(false);
    }
  }, [dateSelected]);

  useEffect(() => {
    setupFormType();
    // eslint-disable-next-line
  }, []);

  return (
    <GuestUserFormContext.Provider
      value={{
        submitForm,
        fieldsValid,

        nameField,
        emailField,

        phoneField,
        phoneCodeField,
        phoneCodeIdField,

        dateField,
        timeField,

        subjectField,
        messageField,

        maxWords,

        handleOnChange,
        handleSubmit,

        applyCountryCode,
      }}
    >
      {children}
    </GuestUserFormContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const GuestUserFormContextImport = () => {
  const context = useContext(GuestUserFormContext);
  if (!context) {
    throw new Error(
      "GuestUserFormContextImport must be used within a GuestUserFormContextProvider"
    );
  }
  return context;
};
